import React, { createContext, useContext, useState, useEffect } from 'react';

const TeamsContext = createContext(null);

export function TeamsProvider({ children }) {
  const [isInTeams, setIsInTeams] = useState(false);
  const [teamsTheme, setTeamsTheme] = useState('default');
  const [teamsContext, setTeamsContext] = useState(null);
  const [teamsReady, setTeamsReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inTeamsParam = params.get('inTeams') === 'true';

    // Check if we're inside an iframe (Teams loads tabs in iframes)
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true; // Cross-origin iframe throws SecurityError
    }

    if (!inTeamsParam && !inIframe) {
      setTeamsReady(true);
      return;
    }

    let cancelled = false;

    async function initTeams() {
      try {
        const teams = await import('@microsoft/teams-js');
        await teams.app.initialize();
        if (cancelled) return;

        setIsInTeams(true);

        const ctx = await teams.app.getContext();
        if (cancelled) return;

        setTeamsContext(ctx);

        // Apply theme from Teams
        const theme = ctx.app?.theme || 'default';
        applyTeamsTheme(theme);
        setTeamsTheme(theme);

        // Listen for theme changes
        teams.app.registerOnThemeChangeHandler((newTheme) => {
          applyTeamsTheme(newTheme);
          setTeamsTheme(newTheme);
        });

        // Notify Teams that the app is loaded
        teams.app.notifySuccess();
      } catch (err) {
        // Not in Teams — that's fine
        console.log('Teams SDK init skipped:', err.message);
      } finally {
        if (!cancelled) setTeamsReady(true);
      }
    }

    initTeams();
    return () => { cancelled = true; };
  }, []);

  return (
    <TeamsContext.Provider value={{ isInTeams, teamsTheme, teamsContext, teamsReady }}>
      {children}
    </TeamsContext.Provider>
  );
}

function applyTeamsTheme(theme) {
  const root = document.documentElement;
  root.classList.remove('dark', 'high-contrast');
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'contrast') {
    root.classList.add('high-contrast');
  }
}

export function useTeams() {
  const ctx = useContext(TeamsContext);
  if (!ctx) throw new Error('useTeams must be used within TeamsProvider');
  return ctx;
}
